import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Heart, MapPin, ExternalLink, Mail, Sparkles } from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { decideRobots, buildCanonical, isOfferDetailIndexable, buildPageMeta } from "@/lib/seoIndex";
import OfferCard from "@/components/OfferCard";
import { getLocalizedOfferTitle, getLocalizedOfferDescription, getOfferSourceLanguageHint } from "@/lib/localizedOffer";
import { trackEvent, safeDomainFromUrl } from "@/lib/analytics";
import { devWarn } from "@/lib/devLog";

export default function OfferDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const [offer, setOffer] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);
  const { user, favorites, toggleFavorite } = useAuth();
  const locale = i18n.language;

  useEffect(() => {
    api.get(`/offers/${id}`)
      .then(r => setOffer(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  // Related-opportunities fetch: progressive fallback.
  // 1. same city + same cause   2. same cause   3. same city   4. recent
  // Excludes the current offer, dedupes by id, limited to 4 items.
  // Lightweight: each /offers query is already paginated server-side.
  useEffect(() => {
    if (!offer) return;
    let cancelled = false;
    const tryQuery = async (params) => {
      try {
        const { data } = await api.get(`/offers?${new URLSearchParams({ limit: 8, ...params })}`);
        return Array.isArray(data) ? data.filter((o) => o.id !== offer.id) : [];
      } catch (err) {
        devWarn("OfferDetailPage: related offers query failed", err);
        return [];
      }
    };
    (async () => {
      const queries = [];
      if (offer.city && offer.cause_area) queries.push({ city: offer.city, cause_area: offer.cause_area });
      if (offer.cause_area) queries.push({ cause_area: offer.cause_area });
      if (offer.city) queries.push({ city: offer.city });
      queries.push({});
      const seen = new Set();
      const collected = [];
      for (const q of queries) {
        if (collected.length >= 4) break;
        const items = await tryQuery(q);
        for (const o of items) {
          if (collected.length >= 4) break;
          if (seen.has(o.id)) continue;
          seen.add(o.id);
          collected.push(o);
        }
      }
      if (!cancelled) setRelated(collected.slice(0, 4));
    })();
    return () => { cancelled = true; };
  }, [offer]);

  const localizedTitle = offer ? getLocalizedOfferTitle(offer, locale) : "";
  const localizedDescription = offer ? getLocalizedOfferDescription(offer, locale) : "";
  const srcHint = offer ? getOfferSourceLanguageHint(offer, locale) : null;

  const detailMeta = offer
    ? buildPageMeta("offerDetail", {
        title: localizedTitle,
        organization: offer.organization,
        description: localizedDescription,
        city: offer.city,
        country: offer.country,
        causeArea: offer.cause_area,
      })
    : buildPageMeta("notFound");

  useSEO({
    robots: notFound
      ? decideRobots({ pageType: "notFound" })
      : isOfferDetailIndexable(offer)
        ? decideRobots({ pageType: "offerDetail" })
        : "noindex, follow",
    title: detailMeta.title,
    description: detailMeta.description,
    canonical: buildCanonical(`/offer/${id}`),
    jsonLd: offer
      ? {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: offer.organization,
          url: offer.url,
          areaServed: offer.country,
          description: (localizedDescription || "").slice(0, 500),
        }
      : undefined,
  });

  if (notFound) return (
    <div className="max-w-3xl mx-auto py-20 px-6 text-center">
      <h1 className="text-2xl font-bold">{t("detail.not_found_title")}</h1>
      <Link to="/offers" className="mt-4 inline-block text-[#5B8C66] font-bold">{t("detail.not_found_link")}</Link>
    </div>
  );

  if (!offer) return <div className="max-w-3xl mx-auto py-20 px-6 text-center text-[#5F6B61]">{t("offers.loading")}</div>;

  const isFav = favorites.has(offer.id);
  const onFav = async () => {
    if (!user) { nav("/login"); return; }
    const willSave = !isFav;
    try {
      await toggleFavorite(offer.id);
    } catch (err) {
      devWarn("OfferDetailPage: toggleFavorite failed", err);
    }
    // Track only the save action (not unsave) and only after the toggle
    // succeeded conceptually. No user id is ever sent.
    if (willSave) {
      trackEvent("Save Opportunity Click", {
        page_type: "offer_detail",
        logged_in: true,
      });
    }
  };

  const onOutboundCta = (surface) => {
    trackEvent("Opportunity CTA Click", {
      opportunity_type: offer.category,
      city: offer.city || undefined,
      cause: offer.cause_area || undefined,
      domain: safeDomainFromUrl(offer.url) || undefined,
      surface,
    });
  };

  const country = t(`countries.${offer.country}`, { defaultValue: offer.country });
  const cause = t(`causes.${offer.cause_area}`, { defaultValue: offer.cause_area });
  const catLabel = t(`categories.${offer.category}`, { defaultValue: offer.category.replace("_"," ") });

  // Visible-on-mobile-only screen-reader hint for external CTAs.
  const externalSrHint = t("detail.opens_new_tab", { defaultValue: "(opens in a new tab)" });

  return (
    <div
      className="max-w-5xl mx-auto px-4 md:px-8 py-10 pb-24 lg:pb-10"
      data-testid="offer-detail-page"
    >
      <Link to="/offers" className="inline-flex items-center gap-1.5 text-sm text-[#5F6B61] hover:text-[#5B8C66] mb-6" data-testid="back-link">
        <ArrowLeft size={14}/> {t("detail.back")}
      </Link>

      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <div className="card-clay overflow-hidden">
            {offer.image_url && (
              <img src={offer.image_url} alt={localizedTitle} className="w-full aspect-[16/10] object-cover"/>
            )}
            <div className="p-6 sm:p-10">
              <div className="flex items-center flex-wrap gap-2 mb-4">
                <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-[#E9F0E0] text-[#3F6147] border border-[#5B8C66]/20">
                  {catLabel}
                </span>
                <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-[#E8F0E2] text-[#3F6147]">
                  {cause}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight leading-[1.1]">{localizedTitle}</h1>
              <div className="mt-3 flex items-center gap-3 text-sm text-[#5F6B61]">
                <span className="font-semibold text-[#1F2A24]">{offer.organization}</span>
                <span>·</span>
                <span className="inline-flex items-center gap-1"><MapPin size={12}/> {offer.city ? `${offer.city}, ` : ""}{country}</span>
              </div>

              {srcHint && (
                <p
                  className="mt-3 text-xs text-[#8A9388]"
                  data-testid="offer-detail-source-lang"
                  title={t("offers.source_language_hint_long", { defaultValue: "Original text in this language" })}
                >
                  {t("offers.source_language_label", { defaultValue: "Original" })}: {srcHint.toUpperCase()}
                </p>
              )}

              <h2 className="mt-8 text-base font-bold uppercase tracking-wider text-[#5F6B61]" data-testid="detail-about-heading">
                {t("detail.about_heading", { defaultValue: "About this opportunity" })}
              </h2>
              <p className="mt-3 text-base leading-relaxed text-[#1F2A24] whitespace-pre-line">{localizedDescription}</p>

              {offer.impact_metric && (
                <div className="mt-7 inline-flex items-center gap-3 rounded-2xl bg-[#E9F0E0] border border-[#5B8C66]/20 px-5 py-3">
                  <Sparkles size={18} className="text-[#3F6147]" strokeWidth={1.6}/>
                  <div>
                    <p className="text-[10px] eyebrow mb-0.5">{t("detail.impact")}</p>
                    <p className="text-sm font-bold text-[#1F2A24]">{offer.impact_metric}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <aside className="lg:col-span-2 space-y-5">
          <div className="card-clay p-6">
            <p className="eyebrow">{t("detail.take_action_eyebrow")}</p>
            <h3 className="mt-2 text-lg font-bold">{t("detail.take_action_title")}</h3>
            <p className="text-sm text-[#5F6B61] mt-2">{t("detail.take_action_body", { org: offer.organization })}</p>

            <div className="mt-5 space-y-3">
              {offer.url && (
                <a
                  href={offer.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  onClick={() => onOutboundCta("detail_aside")}
                  className="btn-pill w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#5B8C66] hover:bg-[#3F6147] text-white font-semibold transition"
                  data-testid="offer-cta-visit"
                >
                  <ExternalLink size={14} aria-hidden="true"/>
                  <span>{t("detail.visit_org")}</span>
                  <span className="sr-only">{externalSrHint}</span>
                </a>
              )}
              {offer.contact_email && (
                <a
                  href={`mailto:${offer.contact_email}`}
                  className="btn-pill w-full inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#DED7C7] hover:border-[#5B8C66] font-semibold transition"
                  data-testid="offer-cta-email"
                >
                  <Mail size={14} aria-hidden="true"/> {t("detail.email_org")}
                </a>
              )}
              <button
                onClick={onFav}
                aria-pressed={isFav}
                className={`btn-pill w-full inline-flex items-center justify-center gap-2 px-5 py-3 font-semibold transition ${isFav ? "bg-[#1F2A24] text-white" : "border border-[#DED7C7] hover:border-[#5B8C66]"}`}
                data-testid="offer-fav-btn"
              >
                <Heart size={14} fill={isFav ? "currentColor" : "none"} aria-hidden="true"/>
                {isFav ? t("detail.saved") : t("detail.save")}
              </button>
            </div>
          </div>

          <div className="card-clay p-6">
            <p className="eyebrow">{t("detail.quick_facts")}</p>
            <dl className="mt-3 grid grid-cols-2 gap-y-3 text-sm">
              <dt className="text-[#5F6B61]">{t("detail.country")}</dt><dd className="font-semibold">{country}</dd>
              {offer.city && (<><dt className="text-[#5F6B61]">{t("detail.city")}</dt><dd className="font-semibold">{offer.city}</dd></>)}
              <dt className="text-[#5F6B61]">{t("detail.category")}</dt><dd className="font-semibold">{catLabel}</dd>
              <dt className="text-[#5F6B61]">{t("detail.cause")}</dt><dd className="font-semibold">{cause}</dd>
            </dl>
          </div>
        </aside>
      </div>

      {/* Related opportunities — prevents dead-end for direct-entry users */}
      {related.length > 0 && (
        <section className="mt-14" data-testid="related-offers">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight">
            {t("detail.related_title", { defaultValue: "More ways to help" })}
          </h2>
          <p className="mt-1 text-sm text-[#5F6B61]">
            {t("detail.related_sub", {
              defaultValue: "Other active opportunities you might find useful.",
            })}
          </p>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {related.map((o) => <OfferCard key={o.id} offer={o} />)}
          </div>
        </section>
      )}

      {/* Mobile-only sticky bottom CTA bar — primary action above the fold. */}
      {offer.url && (
        <div
          className="lg:hidden fixed left-0 right-0 bottom-0 z-30 bg-white/95 backdrop-blur border-t border-[#DED7C7] px-4 py-3 shadow-[0_-4px_12px_rgba(31,42,36,0.06)]"
          data-testid="mobile-sticky-cta"
        >
          <div className="flex items-center gap-2 max-w-5xl mx-auto">
            <button
              onClick={onFav}
              aria-pressed={isFav}
              aria-label={isFav ? t("detail.saved") : t("detail.save")}
              className={`w-12 h-12 rounded-full flex-shrink-0 inline-flex items-center justify-center transition ${
                isFav ? "bg-[#1F2A24] text-white" : "border border-[#DED7C7] text-[#1F2A24]"
              }`}
              data-testid="mobile-sticky-fav-btn"
            >
              <Heart size={18} fill={isFav ? "currentColor" : "none"} aria-hidden="true"/>
            </button>
            <a
              href={offer.url}
              target="_blank"
              rel="noreferrer noopener"
              onClick={() => onOutboundCta("mobile_sticky")}
              className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#5B8C66] hover:bg-[#3F6147] text-white font-semibold transition"
              data-testid="mobile-sticky-cta-visit"
            >
              <ExternalLink size={16} aria-hidden="true"/>
              <span>{t("detail.visit_org")}</span>
              <span className="sr-only">{externalSrHint}</span>
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
